/**
 * React adapter for `@workspace/observability/browser`.
 *
 * Most Next.js apps just need to call `register({ serviceName })` once on
 * the client. This component is the canonical mounting point — drop it
 * into the root `layout.tsx` and the app's browser SDK is live.
 *
 * Convention: `serviceName` follows `<app>-web` to keep the browser tier
 * distinct from the server tier in the Grafana service graph. See
 * tdd-0030.
 *
 * @example
 * ```tsx
 * // apps/<app>/app/layout.tsx
 * import { ObservabilityInit } from "@workspace/observability/browser/react";
 *
 * export default function RootLayout({ children }) {
 *   return (
 *     <html>
 *       <body>
 *         <ObservabilityInit appName="<app>" />
 *         {children}
 *       </body>
 *     </html>
 *   );
 * }
 * ```
 */
"use client";
import { useEffect } from "react";
import { installBrowserErrorCapture, register, } from "./browser.js";
// At module evaluation, not in the effect: this module is in the client
// bundle of every page, so it evaluates before hydration — before the effect
// below runs and long before the lazily-imported SDK arrives. Errors in that
// window (a hydration crash, a chunk that 404s) are buffered and flushed once
// tracing starts (tdd-0110). A no-op on the server.
installBrowserErrorCapture();
export function ObservabilityInit({ appName, config }) {
    useEffect(() => {
        void register({ serviceName: `${appName}-web`, ...config });
        // Init runs once per mount; deps intentionally empty to avoid re-init.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    return null;
}
